import { Box, Image } from '@mantine/core'
import React, { useEffect, useId, useState } from 'react'

import Logo from '@/assets/images/logo.webp'
import QRCodeFallbackImg from '@/assets/images/qr-code-fallback.webp'
import { useSmartScript } from '@/contexts/providers/OneLinkProvider'

import styles from './styles.module.css'

const qrOptions = {
  logo: Logo.src,
  codeColor: '#000',
}

type OneLinkQrCodeProps = {
  isDarkContainer?: boolean
  size?: string
}

export const OneLinkQrCode: React.FC<OneLinkQrCodeProps> = ({
  isDarkContainer = false,
  size = 'calc(100% - 15px)',
}) => {
  const { oneLinkUrl } = useSmartScript()

  const [renderFailed, setRenderFailed] = useState(false)
  // Use a unique ID for each QR code instance to avoid conflicts
  const uniqueId = useId()
  const qrId = `af-onelink-qr-${uniqueId}`

  useEffect(() => {
    // Don't run on server or if URL isn't ready
    if (typeof window === 'undefined' || !oneLinkUrl) {
      if (!oneLinkUrl) {
        // If no URL, fail immediately

        setRenderFailed(true)
      }
      return
    }

    let attempts = 0
    const maxAttempts = 10 // Poll for 3 seconds (10 * 300ms)
    let intervalId: number | null = null

    const tryRender = () => {
      const smartScript = (window as any).AF_SMART_SCRIPT
      const container = document.getElementById(qrId)

      //Check if script and container are ready
      if (smartScript && typeof smartScript.displayQrCode === 'function' && container) {
        if (intervalId) clearInterval(intervalId) // Stop polling

        try {
          // Prevent re-drawing if already rendered
          if (container.querySelector('canvas')) {
            return
          }
          smartScript.displayQrCode(qrId, qrOptions)
        } catch (e) {
          setRenderFailed(true)
        }
      } else {
        attempts++
        if (attempts >= maxAttempts) {
          if (intervalId) clearInterval(intervalId)
          setRenderFailed(true)
        }
      }
    }

    // Start polling
    intervalId = window.setInterval(tryRender, 300)

    // Cleanup function to stop polling if the component unmounts
    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [oneLinkUrl, qrId])

  if (renderFailed) {
    return (
      <Image
        alt="QR Code Fallback"
        className={styles.fallbackImage}
        bg={isDarkContainer ? 'transparent' : 'white'}
        src={QRCodeFallbackImg.src}
      />
    )
  }

  // This container is the target for the smartscript
  return <Box className={styles.qrContainer} id={qrId} style={{ width: size, height: size }} />
}
